import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Color from 'color';
import { StaggeredMotion, Motion } from 'react-motion';
import { showSignup } from '../../actions/user';
import { setRuntimeVariable } from '../../actions/runtime';
import { getLibraryClipCount } from '../../actions/search';
import { FULL_STYLE, RESPONSIVE_SIZE } from '../../constants';
import { imageColors, textColor, themeColors } from '../../lib/Color';
import { springify } from '../../lib/Motion';
import HeadlineAnimator from '../HeadlineAnimator';
import ImageLoader from '../ImageLoader';
import Section from '../Section';
import Button from '../Button';
import Link from '../Link';
import Style from '../Style';
import { L3, H2, H3, P1 } from '../Typography';

const triggerPoints = [0, 0.7, 0.9];
const posterMovementVw = 10;

class SectionHomeTop extends React.PureComponent {
  static propTypes = {
    responsive: PropTypes.number,
    posterClip: PropTypes.object,
    colors: PropTypes.object,
    layoutLoaded: PropTypes.bool,
    marketingVanityAnimation: PropTypes.bool,
    setMarketingVanityAnimation: PropTypes.func,
    onShowSignup: PropTypes.func,
    getLibraryClipCount: PropTypes.func,
  };
  static defaultProps = {};

  constructor(props) {
    super(props);

    this.state = {
      headlineDone: false,
      animatedDefault: [
        { x: props.marketingVanityAnimation ? 1 : 0 },
        { x: props.marketingVanityAnimation ? 1 : 0 },
        { x: props.marketingVanityAnimation ? 1 : 0 },
      ],
      fixedClipCount: 6500,
      libraryClipCount: 6500,
    };
  }

  componentWillMount() {
    this.props
      .getLibraryClipCount()
      .then(count => this.setState({ libraryClipCount: count }));
  }

  setHeadlineDone = () => {
    this.setState({ headlineDone: true });
  };

  getStyles = prevInterpolatedStyles => {
    return [
      springify(
        {
          x: this.state.headlineDone ? 1 : this.state.animatedDefault[0].x,
        },
        { stiffness: 140, damping: 30 },
      ),
      springify({
        x:
          prevInterpolatedStyles[0].x > triggerPoints[1]
            ? 1
            : this.state.animatedDefault[1].x,
      }),
      springify(
        {
          x:
            prevInterpolatedStyles[1].x > triggerPoints[2]
              ? 1
              : this.state.animatedDefault[2].x,
        },
        { stiffness: 60, damping: 14 },
      ),
    ];
  };

  render() {
    const {
      responsive,
      posterClip,
      colors,
      layoutLoaded,
      onShowSignup,
      ...otherProps
    } = this.props;

    if (!posterClip) {
      return null;
    }

    let topSlice = -70;
    let headerTop = 0;
    let headingTopPadding = 13;
    let changesBottomMargin = 1.6;
    let headingMaxWidth = 35;
    let headerFontSize = undefined;
    let showFinally = false;
    let headerBreakLine = true;
    let statementTopMargin = 2;

    let posterWidth = 38;
    let posterBottom = '-3rem';
    let posterHeight = '30rem';
    let posterWordOffset = 0;

    if (responsive >= RESPONSIVE_SIZE.PHONE) {
      topSlice = -60;
    }
    if (responsive >= RESPONSIVE_SIZE.TABLET_PORTRAIT) {
      topSlice = -50;
      headingTopPadding = 20;
      showFinally = true;
      headingMaxWidth = 60;
      headerBreakLine = false;
      posterWidth = 50;
      posterBottom = 'calc(50% - 30vw)';
      posterHeight = '60vw';
      posterWordOffset = -3;
    }
    if (responsive >= RESPONSIVE_SIZE.TABLET_LANDSCAPE) {
      topSlice = -40;
      headingTopPadding = 25;
      headerTop = 2;
      headingMaxWidth = 80;
      posterWordOffset = -9;
    }
    if (responsive >= RESPONSIVE_SIZE.DESKTOP) {
      topSlice = -32;
      changesBottomMargin = 2.5;
      headerFontSize = '7rem';
      statementTopMargin = 3;
      posterWordOffset = -11;
    }

    const animatedCurrent = springify({
      light: this.state.animatedLight,
      dark: this.state.animatedDark,
      poster: this.state.animatedPoster,
    });

    return (
      <StaggeredMotion
        defaultStyles={this.state.animatedDefault}
        styles={this.getStyles}
      >
        {tweens => {
          if (tweens[2].x === 1) {
            setTimeout(this.props.setMarketingVanityAnimation, 0);
          }

          return (
            <div>
              <Section
                style={{
                  position: 'relative',
                  backgroundColor: colors.light,
                  backgroundImage: `linear-gradient(135deg, ${colors.light} 0%,${colors.light} ${tweens[0]
                    .x * 100}%,${Style.Colors.white} ${tweens[0].x *
                    100}%,${Style.Colors.white} 100%)`,
                  minHeight: '62vw',
                }}
              >
                <Section
                  bottomAngle={topSlice}
                  backgroundColor={Style.Colors.white}
                  style={{
                    zIndex: 1,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                  }}
                />
                <div
                  style={{
                    zIndex: 2,
                    position: 'relative',
                    maxWidth: `${headingMaxWidth}rem`,
                    margin: 'auto',
                    padding: `${headingTopPadding}rem 2rem 40vw`,
                    textAlign: 'center',
                  }}
                >
                  <HeadlineAnimator
                    show={layoutLoaded}
                    donePoint={0.75}
                    onDone={this.setHeadlineDone}
                    topContent={
                      <Motion
                        defaultStyle={{ clipCount: this.state.fixedClipCount }}
                        style={springify({
                          clipCount: this.state.libraryClipCount,
                        })}
                      >
                        {tween => {
                          let count = Math.min(
                            Math.round(tween.clipCount),
                            this.state.libraryClipCount,
                          );
                          if (count.toLocaleString) {
                            count = count.toLocaleString();
                          }

                          return (
                            <L3
                              style={{
                                color: Style.Colors.shadow3,
                                display: 'block',
                                margin: `0 auto ${statementTopMargin}rem`,
                              }}
                            >
                              {count} Cinematic Clips&nbsp;&nbsp;•&nbsp;&nbsp;4K
                              ProRes{headerBreakLine && <br />}
                              {!headerBreakLine && (
                                <span>&nbsp;&nbsp;•&nbsp;&nbsp;</span>
                              )}500+ New Clips Each Month
                            </L3>
                          );
                        }}
                      </Motion>
                    }
                    middleContent={
                      <H2 style={{ fontSize: headerFontSize }}>
                        {showFinally ? 'Finally. ' : null}One subscription.
                        Unlimited stock footage downloads.
                      </H2>
                    }
                    bottomContent={
                      <div
                        style={{
                          paddingLeft: '2rem',
                          marginTop: `${statementTopMargin}rem`,
                        }}
                      >
                        <Button
                          border={false}
                          arrow={true}
                          onClick={onShowSignup}
                          style={{
                            fontSize: '2rem',
                            letterSpacing: '2px',
                            fontWeight: 600,
                          }}
                          lightColor={'transparent'}
                        >
                          <span
                            style={{
                              position: 'relative',
                              top: '0.2rem',
                            }}
                          >
                            JOIN STORYTAPE
                          </span>
                        </Button>
                      </div>
                    }
                  />
                </div>
              </Section>

              <Section
                style={{ position: 'relative', zIndex: 4 }}
                angle={17}
                bottomMargin={0}
                backgroundColor={colors.dark}
              >
                {tweens[1].x < 1 && (
                  <div
                    style={{
                      ...FULL_STYLE,
                      transformOrigin: 'top right',
                      transform: 'skewY(-17deg) scale(1.1)',
                      top: '-1px',
                      backgroundImage: `linear-gradient(-90deg, transparent 0%,transparent ${tweens[1]
                        .x * 100}%,${Style.Colors.white} ${tweens[1].x *
                        100}%,${Style.Colors.white} 100%)`,
                    }}
                  />
                )}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    style={{
                      width: `${posterWidth}%`,
                      flexBasis: `${posterWidth}%`,
                      position: 'relative',
                      opacity: tweens[2].x,
                      top: `${tweens[2].x * -1 * posterMovementVw +
                        posterMovementVw}vw`,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: '-1.4vw',
                        right: 0,
                        bottom: posterBottom,
                        height: posterHeight,
                        boxShadow: `0 0 0 1.4vw ${Color(Style.Colors.black)
                          .alpha(0.75)
                          .string()}, 1.4vw -1.4vw 0 1.4vw ${Color(
                          colors.highlight,
                        )
                          .alpha(0.75)
                          .string()}`,
                      }}
                    >
                      <ImageLoader
                        color={imageColors(posterClip.scene_colors).dark}
                        lqip={posterClip.scene_lqip}
                        images={[
                          posterClip.scene_poster,
                          posterClip.scene_thumbnail,
                        ]}
                        imageStyle={{
                          backgroundSize: 'cover',
                          backgroundPosition: `center center`,
                          backgroundRepeat: 'no-repeat',
                        }}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      width: `${100 - posterWidth}%`,
                      flexBasis: `${100 - posterWidth}%`,
                      color: textColor(colors.dark),
                      padding: '0 2rem 0 7vw',
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '50rem',
                        position: 'relative',
                        top: `${posterWordOffset}rem`,
                      }}
                    >
                      <H3 style={{ marginBottom: '1.5rem' }}>
                        Tell a story with complete&nbsp;scenes.
                      </H3>
                      <P1 style={{ maxWidth: '85%' }}>
                        Instead of stand-alone clips, our footage is grouped
                        together in scenes, to help you tell a&nbsp;story.
                      </P1>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    paddingTop:
                      responsive >= RESPONSIVE_SIZE.TABLET_PORTRAIT
                        ? '4rem'
                        : '6rem',
                  }}
                >
                  {responsive >= RESPONSIVE_SIZE.TABLET_PORTRAIT && (
                    <div style={{ width: '50%', flexBasis: '50%' }} />
                  )}
                  <div
                    style={{
                      width: '50%',
                      flexBasis: '50%',
                      padding: '0 2rem 0 7vw',
                      display: 'flex',
                      position: 'relative',
                      top: `${posterWordOffset}rem`,
                      justifyContent:
                        responsive >= RESPONSIVE_SIZE.TABLET_PORTRAIT
                          ? 'flex-start'
                          : 'center',
                    }}
                  >
                    <Link to="/browse">
                      <Button arrow={true} baseColor={colors.dark}>
                        Start&nbsp;Browsing
                      </Button>
                    </Link>
                  </div>
                </div>
              </Section>
            </div>
          );
        }}
      </StaggeredMotion>
    );
  }
}

function mapStateToProps(state, ownProps) {
  return {
    responsive: state.runtime.responsive,
    colors: state.ui.colors,
    layoutLoaded: state.runtime.layoutLoaded,
    marketingVanityAnimation: state.runtime.marketingVanityAnimation,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onShowSignup: () => dispatch(showSignup()),
    setMarketingVanityAnimation: () =>
      dispatch(setRuntimeVariable('marketingVanityAnimation', true)),
    getLibraryClipCount: () => dispatch(getLibraryClipCount()),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SectionHomeTop);



// WEBPACK FOOTER //
// ./src/components/SectionHomeTop/SectionHomeTop.js